/* @section: table-preview */
import type { GeneratedTable, ColumnDef } from "@/lib/types";
import { colLetter } from "@/lib/generator";

interface Props {
  table: GeneratedTable;
}

function formatCell(value: string | number, col: ColumnDef): string {
  if (value === "" || value === null || value === undefined) return "";
  if (col.type === "currency" && typeof value === "number") {
    return value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
  }
  if (col.type === "percent" && typeof value === "number") {
    return (value * 100).toLocaleString("fr-FR", { maximumFractionDigits: 1 }) + " %";
  }
  if (col.type === "number" && typeof value === "number") {
    return value.toLocaleString("fr-FR");
  }
  return String(value);
}

/** Évalue de façon simple une formule d'aperçu (Quantité×Prix, etc.) sur des chiffres connus.
 *  En cas d'impossibilité, renvoie le texte de la formule pour rester transparent. */
function previewFormula(col: ColumnDef): string {
  return col.formula ? col.formula.replace(/\{row\}/g, "n") : "";
}

const TablePreview = ({ table }: Props) => {
  const { columns, rows } = table;
  const previewRows = rows.slice(0, 8);

  return (
    <div className="w-full">
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/60">
              {columns.map((c, i) => (
                <th
                  key={i}
                  className="whitespace-nowrap px-3 py-2.5 text-left font-semibold text-foreground"
                >
                  <span className="flex items-center gap-1.5">
                    {c.name}
                    {c.type === "formula" && (
                      <span className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] font-medium text-primary">
                        fx
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {previewRows.map((row, rIdx) => (
              <tr
                key={rIdx}
                className={rIdx % 2 === 1 ? "bg-muted/30" : "bg-transparent"}
              >
                {columns.map((c, cIdx) => (
                  <td
                    key={cIdx}
                    className={`whitespace-nowrap px-3 py-2 text-foreground/90 ${
                      c.type === "number" || c.type === "currency" || c.type === "percent" || c.type === "formula"
                        ? "text-right font-mono tabular-nums"
                        : ""
                    }`}
                  >
                    {c.type === "formula" ? (
                      <span className="font-mono text-xs text-primary/80">{previewFormula(c)}</span>
                    ) : (
                      formatCell(row[cIdx], c)
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {columns.length} colonne{columns.length > 1 ? "s" : ""} · {rows.length} ligne{rows.length > 1 ? "s" : ""} (aperçu : {previewRows.length})
        </span>
        <span className="flex items-center gap-1">
          <span className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] font-medium text-primary">fx</span>
          colonnes calculées automatiquement
        </span>
      </div>
      {table.note && (
        <p className="mt-3 rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
          {table.note}
        </p>
      )}
    </div>
  );
};

export default TablePreview;
